import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from './api';
import { applyTheme, type ThemeChoice } from './theme';
import { useEventStream } from './useEventStream';
import type { Preferences } from './types';
import Login from './routes/Login';
import Calendar from './routes/Calendar';
import Settings from './routes/Settings';
import Plan from './routes/Plan';

// /api/me always returns 200; user_id is null when not signed in. This keeps
// cold loads from generating 401s that fail2ban watches as attacker signal.
export type Me = {
  user_id: string | null;
  email: string | null;
  identity_account_id: string | null;
  // push_configured tells the SPA whether this server has a public origin
  // wired for Google webhook delivery. False on local dev → events only
  // arrive via the 30-minute slow-poll fallback, which the UI surfaces.
  push_configured: boolean;
};

function useAuth() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/me')
  });
}

// Hydrate the user-scoped theme from the server once they're signed in.
// theme-init.js paints with the localStorage-cached theme to avoid a flash;
// after the preferences fetch returns, we overwrite the cache so the next
// load on this device starts in the right theme.
function useServerTheme(enabled: boolean) {
  const q = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.get<Preferences>('/api/preferences'),
    enabled,
    // The theme can change outside this tab (theme-set writes the row over
    // psql), which arrives as a stream message. Focus refetch is the fallback
    // for when the stream is down; both need a zero staleTime to refetch at all.
    staleTime: 0,
    refetchOnWindowFocus: true
  });
  useEffect(() => {
    const t = q.data?.theme as ThemeChoice | undefined;
    if (t) applyTheme(t);
  }, [q.data?.theme]);
}

function Protected({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useAuth();
  useServerTheme(!!data?.user_id);
  useEventStream(!!data?.user_id);
  if (isLoading) return <div className="grid h-dvh place-items-center text-text-faint">…</div>;
  if (!data?.user_id) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <Protected>
            <Calendar />
          </Protected>
        }
      />
      <Route
        path="/settings"
        element={
          <Protected>
            <Settings />
          </Protected>
        }
      />
      <Route
        path="/plan"
        element={
          <Protected>
            <Plan />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
